import { useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api-class';
import type { FormSchemaSearchableFieldsData } from '@/services/api-types';

export interface UseSearchableFormFieldsOptions {
  /** e.g. 'CLIENT'. Pass `null` to skip loading until set. */
  attachTo: string | null;
  /** Auto-load when `attachTo` is non-null. Default `true`. */
  autoLoad?: boolean;
}

export interface UseSearchableFormFieldsResult {
  /**
   * One entry per active schema with at least one searchable field. Already
   * filtered by attachTo. Field order matches the admin's editor layout.
   */
  schemas: FormSchemaSearchableFieldsData[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetches the lean searchable-fields projection used by Records Search to
 * render its per-schema filter sections. Wraps `api.getSearchableFormFields`,
 * which hits a dedicated endpoint that returns only what the search UI needs
 * (key/label/type/options) — never the full schema definition.
 *
 * Use this instead of `useFormSchemas` whenever the consumer just needs to
 * list filterable fields. `useFormSchemas` exists for the schema-list grid
 * (Custom Forms admin screen) and intentionally returns a slim view without
 * field details.
 */
export function useSearchableFormFields({
  attachTo,
  autoLoad = true,
}: UseSearchableFormFieldsOptions): UseSearchableFormFieldsResult {
  const [schemas, setSchemas] = useState<FormSchemaSearchableFieldsData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (attachTo == null) {
      setSchemas([]);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    api.getSearchableFormFields(
      attachTo,
      (result: FormSchemaSearchableFieldsData[] | null | undefined) => {
        setSchemas(Array.isArray(result) ? result : []);
        setIsLoading(false);
      },
      (err: any) => {
        setError(err?.message ?? 'Failed to load searchable form fields');
        setSchemas([]);
        setIsLoading(false);
      },
    );
  }, [attachTo]);

  useEffect(() => {
    if (!autoLoad) return;
    load();
  }, [autoLoad, load]);

  return {
    schemas,
    isLoading,
    error,
    reload: load,
  };
}
